// SharePage — página /share/:id em modo reflexão.
// SEM header, SEM bottom nav, SEM menus. Experiência imersiva.
// Renderizada por rota dedicada em App.jsx sem o Layout global.

import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowRight, Share2 } from "lucide-react";
import { api } from "../../lib/api";
import { trackShareEvent } from "./ShareAnalytics";
import { generateShareCard } from "./ShareCardGenerator";
import ShareButton from "./ShareButton";

export default function SharePage() {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/share/${encodeURIComponent(id)}`)
      .then((r) => {
        if (cancelled) return;
        setState({ loading: false, data: r.data, error: null });
        trackShareEvent("share_page_opened", { share_id: id });
        // OG-ish meta injection (client-side; SSR virá na Fase 2)
        try {
          const d = r.data?.share_data || {};
          const title = d.title || "Reflexão do dia";
          const desc = d.main_phrase || d.verse || "Reflexão do App CM";
          setDocMeta({ title: `${title}, CM`, description: desc.slice(0, 160), shareId: id });
        } catch { /* noop */ }
      })
      .catch((e) => {
        if (cancelled) return;
        setState({ loading: false, data: null, error: e?.response?.status || "erro" });
      });
    return () => { cancelled = true; };
  }, [id]);

  // gera a imagem do card para exibir no topo
  useEffect(() => {
    if (!state.data) return;
    (async () => {
      try {
        const blob = await generateShareCard({
          format: "feed",
          template: state.data.template || "contemplacao",
          data: state.data.share_data || {},
          sharedBy: state.data.shared_by,
        });
        setImageUrl(URL.createObjectURL(blob));
      } catch { /* noop */ }
    })();
  }, [state.data]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center text-gold">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen bg-navy flex flex-col items-center justify-center text-cream px-6 text-center">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Reflexão indisponível</p>
        <p className="mt-4 font-display text-2xl">Este link expirou ou não existe mais.</p>
        <Link
          to="/"
          className="mt-8 inline-flex items-center gap-2 rounded-full border border-gold/50 text-gold px-5 py-2 text-[11px] uppercase tracking-widest hover:bg-gold/10"
        >
          Voltar ao início <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    );
  }

  const d = state.data.share_data || {};
  const contentType = state.data.content_type;
  const continueLink = _buildContinueLink(contentType, state.data.content_id);

  return (
    <div className="min-h-screen bg-navy text-cream" data-testid="share-page">
      <div className="max-w-2xl mx-auto px-5 pt-10 pb-16 sm:px-8 sm:pt-14">
        {/* logo discreto */}
        <div className="flex items-center gap-2 opacity-80">
          <span className="font-display text-2xl text-gold">CM</span>
          <span className="text-[11px] uppercase tracking-[0.3em] text-cream/60">
            Bíblia com Propósito
          </span>
        </div>

        <div className="gold-rule my-8" />

        {/* imagem gerada */}
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Card da reflexão"
            data-testid="share-page-card"
            className="w-full rounded-2xl border border-gold/20 shadow-2xl"
          />
        )}

        {/* conteúdo completo */}
        <article className="mt-10 space-y-6">
          {d.theme && (
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">✦ {d.theme}</p>
          )}
          {d.title && (
            <h1 className="font-display text-3xl sm:text-4xl leading-tight text-cream" data-testid="share-page-title">
              {d.title}
            </h1>
          )}
          {d.main_phrase && (
            <blockquote className="border-l-2 border-gold pl-5 text-lg leading-relaxed text-cream italic">
              &ldquo;{d.main_phrase}&rdquo;
            </blockquote>
          )}

          {(d.verse || d.verse_ref) && (
            <section>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">📖 Versículo no contexto</p>
              {d.verse && <p className="text-cream leading-relaxed">{d.verse}</p>}
              {d.verse_ref && <p className="mt-2 text-gold font-medium">{d.verse_ref}</p>}
            </section>
          )}

          {d.application && (
            <section>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">💡 Aplicação para hoje</p>
              <p className="text-cream leading-relaxed">{d.application}</p>
            </section>
          )}

          {d.prayer && (
            <section>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">🙏 Oração</p>
              <p className="text-cream leading-relaxed italic">{d.prayer}</p>
            </section>
          )}
        </article>

        <div className="gold-rule my-10" />

        <p className="text-xs uppercase tracking-widest text-cream/55" data-testid="share-page-shared-by">
          Compartilhado por {state.data.shared_by || "App CM"}
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Link
            to={continueLink.pathname}
            data-testid="share-continue-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-gold text-navy px-6 py-3 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-gold/90 transition-colors"
          >
            Continue sua caminhada <ArrowRight className="w-4 h-4" />
          </Link>
          <div data-testid="share-page-share-again">
            <ShareButton
              variant="pill"
              label="Compartilhar esta reflexão"
              contentType={contentType}
              contentId={state.data.content_id}
              title={d.title}
              mainText={d.main_phrase}
              verse={d.verse}
              verseRef={d.verse_ref}
              application={d.application}
              prayer={d.prayer}
              theme={d.theme}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function _buildContinueLink(contentType, contentId) {
  switch (contentType) {
    case "devocional":
      return { pathname: `/devocional` };
    case "sermon":
      return { pathname: contentId ? `/sermoes/${contentId}` : `/sermoes` };
    case "plan":
      return { pathname: contentId ? `/planos/${contentId}` : `/planos` };
    case "verse":
      return { pathname: `/biblia` };
    case "prayer":
      return { pathname: `/oracao` };
    default:
      return { pathname: `/` };
  }
}

function setDocMeta({ title, description, shareId }) {
  if (typeof document === "undefined") return;
  if (title) document.title = title;
  const setMeta = (attr, name, content) => {
    let el = document.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, name);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };
  if (title) {
    setMeta("property", "og:title", title);
    setMeta("name", "twitter:title", title);
  }
  if (description) {
    setMeta("property", "og:description", description);
    setMeta("name", "twitter:description", description);
    setMeta("name", "description", description);
  }
  const url = typeof window !== "undefined" ? window.location.href : "";
  if (url) setMeta("property", "og:url", url);
  setMeta("property", "og:type", "article");
  setMeta("property", "og:site_name", "C E Medeiros, Bíblia com Propósito");
  setMeta("property", "og:locale", "pt_BR");
  setMeta("name", "twitter:card", "summary_large_image");
  if (shareId) {
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const ogImg = `${apiBase}/api/share/${encodeURIComponent(shareId)}/og-image.png`;
    setMeta("property", "og:image", ogImg);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("name", "twitter:image", ogImg);
  }
}
